// Layer 1 display discipline — controls when the per-message coaching
// insight (the "Why this tone?" card in AnalysisCard) is shown, and what
// fallback line to surface when the backend returns no specific guidance.
//
// ADDITIVE module — does not touch sessionLog, the existing phrase
// libraries (registerPhraseLibrary, phraseLibrary, signal tip map in
// insights.js), or the backend response shape. Pure frontend display
// logic + a small per-chat history kept under a dedicated localStorage
// key so it never collides with the session record.
//
// ─────────────────────────────────────────────────────────────────────
// SPEC RULES IMPLEMENTED
// ─────────────────────────────────────────────────────────────────────
//
// FIX 1 — REGENERATE SUPPRESSION
//   Owned by the call site in ToneGuardPage's regenerate handler — see
//   the comment there. This module never recomputes a flag during a
//   regenerate-only state change because `analysis.id` (the stable
//   per-original-message id) stays the same; `markDisplayed()` keeps
//   a "lastMarkedAnalysisId" guard so re-renders cannot double-bump
//   the history either.
//
// FIX 2 — FALLBACK FLAG `general_awareness`
//   Triggered when `analysis.coaching` is empty AND `analysis.rationale`
//   is missing — i.e. the backend returned no specific Layer 1 guidance
//   for this (intent × signal) combination. We surface a closed-library
//   style-only line instead of going silent.
//
//   Display frequency: max once every 3 messages — if the previous
//   displayed flag was `general_awareness`, we suppress on the current
//   message too. Prevents the generic line from feeling like filler.
//
// FIX 3 — CONSECUTIVE REPETITION GUARD
//   If the same flag fires on 3+ consecutive messages (any flag, not
//   just general_awareness) → suppress on the 3rd+ occurrence and
//   resume display once a different flag fires. Prevents identical
//   Layer 1 lines from nagging the user.
//
// Layers 2 / 3 / 4 are UNAFFECTED — these rules apply to Layer 1 only.
// No backend, no LLM, no free-form generation.

const LAYER1_HISTORY_KEY_PREFIX = "toneguard:layer1History:";
const MAX_HISTORY = 8; // enough to evaluate "3 consecutive" comfortably

// Closed-library general_awareness lines — style only, no intent-specific
// guidance. Mirrors the spec table verbatim.
const GENERAL_AWARENESS_OUTPUTS = {
    calm: "Worth a brief pause before sending this.",
    direct: "Clear message — a quick re-read can sharpen it.",
    warm: "This reads warmly — a small check-in line could add to it.",
    professional:
        "Solid message — a brief review for tone consistency helps.",
};

// ─────────────────────────────────────────────────────────────────────
// Love-intent additions (additive — does NOT touch the existing 6
// intents, layers 2/3/4, or session record schema).
// ─────────────────────────────────────────────────────────────────────
//
// Detection: when the user explicitly selected `intent === "love"` AND
// the backend's detected_signal matches one of the spec's signal sets,
// produce a Love-specific flag that takes precedence over the default
// combo / general_awareness flags.
//
// HIGH risk signals → soften_for_love.
// MED-cold signal   → warm_up_affection.
// MED-tension set   → ease_love_tension.
// Anything else with Love intent → fall through to the default Layer 1
// detection (combo flag or general_awareness fallback).

const LOVE_FLAG_SOFTEN = "love_soften_for_love";
const LOVE_FLAG_WARM = "love_warm_up_affection";
const LOVE_FLAG_TENSION = "love_ease_love_tension";

const LOVE_SIGNAL_TO_FLAG = {
    // HIGH risk
    accusatory: LOVE_FLAG_SOFTEN,
    contemptuous: LOVE_FLAG_SOFTEN,
    dismissive: LOVE_FLAG_SOFTEN,
    passive_aggressive: LOVE_FLAG_SOFTEN,
    threatening: LOVE_FLAG_SOFTEN,
    // MED — cold (its own bucket per spec)
    cold: LOVE_FLAG_WARM,
    // MED — tension
    defensive: LOVE_FLAG_TENSION,
    pressuring: LOVE_FLAG_TENSION,
    frustrated: LOVE_FLAG_TENSION,
    abrupt: LOVE_FLAG_TENSION,
};

// Closed-library Layer 1 lines for Love flags. Keyed by [flag][userStyle].
// Spec verbatim — DO NOT free-form or paraphrase.
const LOVE_FLAG_OUTPUTS = {
    [LOVE_FLAG_SOFTEN]: {
        calm:
            "This carries more edge than warmth right now — worth softening.",
        direct:
            "The point lands — but this isn't how love usually sounds from you.",
        warm:
            "This doesn't feel like your usual warmth — a gentler tone would land better.",
        professional:
            "This reads sharper than intended for a personal message.",
    },
    [LOVE_FLAG_WARM]: {
        calm: "A little more warmth would make this feel more like you.",
        direct: "Clear, but a touch of warmth wouldn't hurt here.",
        warm: "This feels cooler than your usual affectionate tone.",
        professional:
            "A warmer note would suit this kind of message better.",
    },
    [LOVE_FLAG_TENSION]: {
        calm:
            "The feeling is valid — a calmer tone will help it land as care, not criticism.",
        direct:
            "The frustration is real — but love messages land better without the edge.",
        warm:
            "This reads more tense than loving — softening it could help.",
        professional:
            "Consider easing the tension here — this is a personal message.",
    },
};

// Register modifier lines (closed library, additive). Only fire when a
// Love flag fires AND a relationship_register is set. Authority is
// intentionally absent — per spec, "Love intent rarely applies to
// Authority; treat as Peer modifier" — so when register === "work" we
// fall back to the Peer line.
const LOVE_FLAG_REGISTER_LINES = {
    [LOVE_FLAG_SOFTEN]: {
        direct: {
            close:
                "This will land harder than you mean it to — lead with how you feel, not what they did.",
            family:
                "This carries old tension — softer phrasing protects the relationship.",
            friend:
                "Still loving underneath — let that come through more than the frustration.",
        },
    },
    [LOVE_FLAG_WARM]: {
        calm: {
            close:
                "A small warm note here would mean a lot — this feels a bit distant for the two of you.",
            family:
                "Family messages like this land better with a touch more warmth.",
            friend:
                "This is for someone close — a little more warmth fits the relationship.",
        },
    },
};

function isLoveFlag(flag) {
    return (
        flag === LOVE_FLAG_SOFTEN ||
        flag === LOVE_FLAG_WARM ||
        flag === LOVE_FLAG_TENSION
    );
}

function resolveLoveFlag(analysis) {
    if (!analysis || analysis.intent !== "love") return null;
    const signal = analysis.detected_signal;
    if (!signal) return null;
    return LOVE_SIGNAL_TO_FLAG[signal] || null;
}

function loveRegisterLine(flag, userStyle, register) {
    if (!flag || !userStyle || !register) return null;
    const byStyle = LOVE_FLAG_REGISTER_LINES[flag];
    if (!byStyle) return null;
    const byRegister = byStyle[userStyle];
    if (!byRegister) return null;
    // Authority → Peer per spec note.
    const lookupKey = register === "work" ? "friend" : register;
    return byRegister[lookupKey] || null;
}

// Compute a stable, deterministic flag string from the analysis result.
// Used both for repetition tracking AND for branching into general_awareness
// or one of the Love-intent flags.
//
// `null` means "no analysis to track" (e.g., before submission). Anything
// else is one of:
//   • general_awareness  — backend returned no specific guidance
//   • love_soften_for_love / love_warm_up_affection / love_ease_love_tension
//     — Love intent + matching detected signal (closed library)
//   • combo:{intent}:{signal} — default Layer 1 detection
export function computeLayer1Flag(analysis) {
    if (!analysis) return null;
    // Love-intent specific detection runs BEFORE the general checks so a
    // matching Love flag takes precedence even when the backend ALSO
    // returned coaching/rationale. Spec rule: these are additive flags
    // surfacing the closed-library line.
    const loveFlag = resolveLoveFlag(analysis);
    if (loveFlag) return loveFlag;
    const hasCoaching =
        Array.isArray(analysis.coaching) && analysis.coaching.length > 0;
    const hasRationale =
        typeof analysis.rationale === "string" &&
        analysis.rationale.trim().length > 0;
    if (!hasCoaching && !hasRationale) return "general_awareness";
    const intent = analysis.intent || "noIntent";
    const signal = analysis.detected_signal || "noSignal";
    return `combo:${intent}:${signal}`;
}

// ── History store (per-chat, localStorage) ─────────────────────────────
//
// Shape: { lastMarkedAnalysisId: string|null, flags: string[] }
//   • flags: chronological list of flags that were ACTUALLY DISPLAYED
//     (not just computed) for that chat, oldest → newest, capped at
//     MAX_HISTORY entries.
//   • lastMarkedAnalysisId: guard against React StrictMode + re-render
//     double-bumps. Only the FIRST mark for a given analysis.id counts.

function historyKey(chatId) {
    return `${LAYER1_HISTORY_KEY_PREFIX}${chatId || "default"}`;
}

function readHistory(chatId) {
    if (typeof window === "undefined" || !chatId) {
        return { lastMarkedAnalysisId: null, flags: [] };
    }
    try {
        const raw = window.localStorage.getItem(historyKey(chatId));
        if (!raw) return { lastMarkedAnalysisId: null, flags: [] };
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
            return {
                lastMarkedAnalysisId: parsed.lastMarkedAnalysisId || null,
                flags: Array.isArray(parsed.flags) ? parsed.flags : [],
            };
        }
    } catch {
        /* silent — quota / private-mode */
    }
    return { lastMarkedAnalysisId: null, flags: [] };
}

function writeHistory(chatId, state) {
    if (typeof window === "undefined" || !chatId) return;
    try {
        window.localStorage.setItem(historyKey(chatId), JSON.stringify(state));
    } catch {
        /* silent */
    }
}

// Resolve the user-facing communication style key (calm | direct | warm
// | professional). Caller passes this in — we don't reach back into the
// store from this module.
function resolveStyleKey(currentStyle) {
    if (
        currentStyle === "calm" ||
        currentStyle === "direct" ||
        currentStyle === "warm" ||
        currentStyle === "professional"
    ) {
        return currentStyle;
    }
    // Fallback when no style is set yet — use "calm" lines as the most
    // neutral default. (Spec is silent on null-style, this is conservative.)
    return "calm";
}

/**
 * Decide whether the Layer 1 insight section should render for the current
 * analysis, and what to surface inside it.
 *
 * @param {object} args
 * @param {string} args.chatId         The active chat id (used to scope history).
 * @param {object|null} args.analysis  The current analysis result.
 * @param {string|null} args.currentStyle  User-facing style key.
 * @param {string|null} [args.register]    Optional chat.relationship_register
 *                                          — when set, Love flags also pick
 *                                          a closed-library register modifier
 *                                          line ("Intimate" / "Familial" /
 *                                          "Peer"; Authority → Peer per spec).
 * @returns {{
 *   shouldDisplay: boolean,
 *   flag: string|null,
 *   fallbackLine: string|null,
 *   loveLine: string|null,
 *   loveRegisterLine: string|null,
 *   suppressionReason: 'none' | 'general_awareness_cooldown' | 'consecutive_repetition' | 'no_flag',
 * }}
 */
export function resolveLayer1Display({
    chatId,
    analysis,
    currentStyle,
    register = null,
}) {
    const flag = computeLayer1Flag(analysis);
    if (!flag) {
        return {
            shouldDisplay: false,
            flag: null,
            fallbackLine: null,
            loveLine: null,
            loveRegisterLine: null,
            suppressionReason: "no_flag",
        };
    }

    const { flags } = readHistory(chatId);

    // Fix 3 — consecutive repetition guard. If the last 2 displayed
    // flags both equal the current one, suppress (this would be the
    // 3rd consecutive). Applies to ALL flags including the Love set.
    const lastTwo = flags.slice(-2);
    if (
        lastTwo.length === 2 &&
        lastTwo[0] === flag &&
        lastTwo[1] === flag
    ) {
        return {
            shouldDisplay: false,
            flag,
            fallbackLine: null,
            loveLine: null,
            loveRegisterLine: null,
            suppressionReason: "consecutive_repetition",
        };
    }

    // Fix 2 — general_awareness frequency rule. Max once every 3 messages
    // → if the most recent displayed flag was also general_awareness,
    // suppress.
    if (flag === "general_awareness") {
        const lastDisplayed = flags[flags.length - 1] || null;
        if (lastDisplayed === "general_awareness") {
            return {
                shouldDisplay: false,
                flag,
                fallbackLine: null,
                loveLine: null,
                loveRegisterLine: null,
                suppressionReason: "general_awareness_cooldown",
            };
        }
        const styleKey = resolveStyleKey(currentStyle);
        return {
            shouldDisplay: true,
            flag,
            fallbackLine:
                GENERAL_AWARENESS_OUTPUTS[styleKey] ||
                GENERAL_AWARENESS_OUTPUTS.calm,
            loveLine: null,
            loveRegisterLine: null,
            suppressionReason: "none",
        };
    }

    // Love intent → closed-library line + optional register modifier.
    if (isLoveFlag(flag)) {
        const styleKey = resolveStyleKey(currentStyle);
        const line =
            LOVE_FLAG_OUTPUTS[flag]?.[styleKey] ||
            LOVE_FLAG_OUTPUTS[flag]?.calm ||
            null;
        return {
            shouldDisplay: true,
            flag,
            fallbackLine: null,
            loveLine: line,
            loveRegisterLine: loveRegisterLine(flag, styleKey, register),
            suppressionReason: "none",
        };
    }

    // Default — display the backend-provided coaching/rationale.
    return {
        shouldDisplay: true,
        flag,
        fallbackLine: null,
        loveLine: null,
        loveRegisterLine: null,
        suppressionReason: "none",
    };
}

/**
 * Persist the fact that this analysis's flag was ACTUALLY surfaced. Safe
 * to call multiple times for the same analysis.id — the lastMarkedAnalysisId
 * guard makes subsequent calls a no-op (so React StrictMode + re-renders
 * don't inflate the history).
 *
 * @param {object} args
 * @param {string} args.chatId
 * @param {string|null} args.analysisId  Stable id of the current original message.
 * @param {string|null} args.flag        From resolveLayer1Display().
 */
export function markLayer1Displayed({ chatId, analysisId, flag }) {
    if (!chatId || !analysisId || !flag) return;
    const state = readHistory(chatId);
    if (state.lastMarkedAnalysisId === analysisId) return; // idempotent
    const nextFlags = [...state.flags, flag].slice(-MAX_HISTORY);
    writeHistory(chatId, {
        lastMarkedAnalysisId: analysisId,
        flags: nextFlags,
    });
}

// Test surface.
export const __internals = {
    LAYER1_HISTORY_KEY_PREFIX,
    GENERAL_AWARENESS_OUTPUTS,
    LOVE_FLAG_OUTPUTS,
    LOVE_FLAG_REGISTER_LINES,
    LOVE_SIGNAL_TO_FLAG,
    LOVE_FLAG_SOFTEN,
    LOVE_FLAG_WARM,
    LOVE_FLAG_TENSION,
    MAX_HISTORY,
    readHistory,
    writeHistory,
    historyKey,
    resolveStyleKey,
    isLoveFlag,
    resolveLoveFlag,
    loveRegisterLine,
};
